import React from 'react';
import PropTypes from 'prop-types';
import uniqid from 'uniqid';
import { Box, Text } from 'grommet';
import { localAsset } from '@Helpers/asset';
import { useAppTheme } from '@Components/Context';
import {
  ProductFormCheckBoxInput,
  ProductFormSingleTextInput,
} from '@Components/Partial/Product/NewSearch';
import AppButton from './AppButton';
import {
  StyledFlexibleDrop, StyledSelectMenu, StyledSVG,
} from './StyledButtons';

const FlexibleSelectDropdown = ({
  textColor,
  handleItemSelect,
  flexibleOptions,
  noSelectionLabel,
  displayKey,
  valueKey,
  noBorder = false,
  borderColor = null,
  buttonBg = null,
  iconColor = null,
  buttonWidth = null,
  buttonHeight = 'var(--table-controls-height)',
  buttonJustify = null,
  withReset = false,
  selectMenuBg = null,
  selectMenuHighlight = null,
  selectedOption = null,
  customPad = null,
  customRadius = null,
  iconPath = null,
  multiSelect = false,
  withSearch = false,
  ...rest
}) => {
  const { theme: { page, button } } = useAppTheme();
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [multiSelected, setMultiSelected] = React.useState(
    Array.isArray(selectedOption) ? selectedOption : [],
  );
  // Track if user made changes while open
  const [hasStagedChange, setHasStagedChange] = React.useState(false);

  React.useEffect(() => {
    if (multiSelect && Array.isArray(selectedOption) && !isOpen) {
      setMultiSelected(selectedOption);
      setHasStagedChange(false);
    }
  }, [selectedOption, multiSelect, isOpen]);

  // When menu closes, trigger API if staged changes exist
  const handleMenuClose = () => {
    setIsOpen(false);
    setSearchTerm('');
    if (multiSelect && hasStagedChange) {
      handleItemSelect(multiSelected);
      setHasStagedChange(false);
    }
  };

  const handleMultiSelect = (optionValue, checked) => {
    let updated;
    if (checked) {
      updated = [...multiSelected, optionValue];
    } else {
      updated = multiSelected.filter((v) => v !== optionValue);
    }
    setMultiSelected(updated);
    setHasStagedChange(true);
  };

  const handleReset = () => {
    setMultiSelected([]);
    setHasStagedChange(false);
    handleItemSelect([]);
    setIsOpen(false);
  };

  const handleFinished = () => {
    handleItemSelect(multiSelected);
    setHasStagedChange(false);
    setIsOpen(false);
  };

  const renderDropMenu = (onClose) => {
    if (multiSelect) {
      return (
        <Box round="var(--app-radius)" pad="12px" background={buttonBg ?? page.inputBg}>
          {flexibleOptions?.map((option) => (
            <Box pad={{ vertical: customPad || '0.5rem' }} key={option[valueKey]}>
              <ProductFormCheckBoxInput
                borderColor={page.inputBorder}
                focusHighlight={page.focusHighlight}
                checkmarkColor={page.inputIcon}
                label={option[displayKey]}
                labelColor={page.primaryText}
                labelSize="0.95rem"
                value={multiSelected.includes(option[valueKey])}
                controlled
                handleFormValues={(checked) => handleMultiSelect(option[valueKey], checked)}
                customBoxSize="1.25rem"
                customCheckmarkSize="1.1rem"
              />
            </Box>
          ))}
          {hasStagedChange && (
            <Box pad={{ top: '0.5rem' }}>
              <StyledSelectMenu
                plain
                key="finished"
                label="Finished"
                onClick={handleFinished}
                hoverIndicator={selectMenuBg ?? button.hoverBg}
                highlightText={selectMenuHighlight ?? button.hoverText}
              />
            </Box>
          )}
          {withReset && multiSelected.length > 0 && (
            <Box pad={{ top: '0.5rem' }}>
              <StyledSelectMenu
                plain
                key="reset"
                label="Reset"
                onClick={handleReset}
                hoverIndicator={selectMenuBg ?? button.hoverBg}
                highlightText={selectMenuHighlight ?? button.hoverText}
              />
            </Box>
          )}
        </Box>
      );
    }

    const visibleOptions = withSearch && searchTerm
      ? flexibleOptions?.filter((opt) => (
        String(opt[displayKey]).toLowerCase().includes(searchTerm.toLowerCase())
      ))
      : flexibleOptions;

    // Single select menu (default)
    return (
      <Box direction="column" width={{ min: '160px' }}>
        {withSearch && (
          <Box
            height={{ min: 'fit-content' }}
            pad={{ horizontal: '0.5rem', top: '0.5rem', bottom: '0.5rem' }}
            background={buttonBg ?? page.inputBg}
            round={{ corner: 'top', size: 'var(--app-radius)' }}
          >
            <ProductFormSingleTextInput
              focusHighlight={page.focusHighlight}
              value={searchTerm}
              handleFormValues={setSearchTerm}
              placeholder="Search..."
            />
          </Box>
        )}
        <Box
          round={withSearch ? { corner: 'bottom', size: 'var(--app-radius)' } : 'var(--app-radius)'}
          background={buttonBg ?? page.inputBg}
          overflow={{ vertical: 'auto' }}
          height={{ max: '280px' }}
          pad="8px"
        >
          {visibleOptions?.map((option) => (
            <StyledSelectMenu
              plain
              key={uniqid()}
              label={option[displayKey]}
              onClick={() => {
                handleItemSelect(option[valueKey]);
                setSearchTerm('');
                onClose();
              }}
              hoverIndicator={selectMenuBg ?? button.hoverBg}
              highlightText={selectMenuHighlight ?? button.hoverText}
            />
          ))}
          {withReset && selectedOption && (
            <StyledSelectMenu
              plain
              key="reset"
              label="Reset"
              onClick={() => {
                handleItemSelect(null);
                setSearchTerm('');
                onClose();
              }}
              hoverIndicator={selectMenuBg ?? button.hoverBg}
              highlightText={selectMenuHighlight ?? button.hoverText}
            />
          )}
        </Box>
      </Box>
    );
  };

  // Display selected values
  const getSelectedLabel = () => {
    if (multiSelect) {
      if (multiSelected.length === 0) return noSelectionLabel;
      if (multiSelected.length === 1) {
        const found = flexibleOptions.find((opt) => opt[valueKey] === multiSelected[0]);
        return found ? found[displayKey] : multiSelected[0];
      }
      return `${multiSelected.length} selected`;
    }
    if (selectedOption !== null && selectedOption !== undefined) {
      const found = flexibleOptions?.find((opt) => opt[valueKey] === selectedOption);
      return found ? found[displayKey] : selectedOption;
    }
    return noSelectionLabel;
  };

  return (
    <StyledFlexibleDrop
      formIcons
      height={buttonHeight}
      width={buttonWidth}
      bgColor={buttonBg}
      noBorder={noBorder}
      customPad={customPad}
      customRadius={customRadius}
      borderColor={borderColor}
      highlight={page.focusHighlight}
      onOpen={() => setIsOpen(true)}
      onClose={handleMenuClose}
      isOpen={isOpen}
      withCloseDropContent={renderDropMenu}
      {...rest}
    >
      {(Icon) => (
        <AppButton
          fill
          dropDown
          textSize="1rem"
          color={textColor}
        >
          <Box>
            <Box direction="row" gap="0.5rem" width="100%" justify={buttonJustify} align="center">
              <Box direction="row" gap="0.5rem" align="center">
                {iconPath && (
                  <Box pad={{ top: '0.2rem' }}>
                    <StyledSVG
                      src={localAsset(iconPath)}
                      width="1rem"
                      height="1rem"
                      $fillColor={iconColor ?? page.inputIcon}
                      title={displayKey}
                    />
                  </Box>
                )}
                <Text color={textColor} size="0.9rem">
                  {getSelectedLabel()}
                </Text>
              </Box>
              <Box pad={{ top: '0.15rem' }}>
                <Icon color={iconColor ?? page.inputIcon} size="0.9rem" />
              </Box>
            </Box>
          </Box>
        </AppButton>
      )}
    </StyledFlexibleDrop>
  );
};

FlexibleSelectDropdown.propTypes = {
  textColor: PropTypes.string.isRequired,
  selectedOption: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  handleItemSelect: PropTypes.func.isRequired,
  displayKey: PropTypes.string.isRequired,
  valueKey: PropTypes.string.isRequired,
  flexibleOptions: PropTypes.arrayOf(PropTypes.any.isRequired).isRequired,
  noSelectionLabel: PropTypes.string.isRequired,
  noBorder: PropTypes.bool,
  borderColor: PropTypes.string,
  buttonBg: PropTypes.string,
  buttonWidth: PropTypes.string,
  buttonHeight: PropTypes.string,
  buttonJustify: PropTypes.string,
  customPad: PropTypes.string,
  customRadius: PropTypes.string,
  withReset: PropTypes.bool,
  selectMenuBg: PropTypes.string,
  selectMenuHighlight: PropTypes.string,
  iconPath: PropTypes.string,
  iconColor: PropTypes.string,
  multiSelect: PropTypes.bool,
  withSearch: PropTypes.bool,
};

export default FlexibleSelectDropdown;
